import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Shield } from "lucide-react";
import logo from "@/assets/logo-mente-sa.png";

const AdminSetup = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("create-admin", {
        body: { email, password },
      });

      let fnError: string | undefined = data?.error;
      if (error) {
        try {
          const body = await (error as any).context?.json?.();
          fnError = body?.error || error.message;
        } catch {
          fnError = error.message;
        }
      }

      const adminExists = fnError?.includes("já existe");
      if (fnError && !adminExists) {
        toast({ title: "Erro", description: fnError, variant: "destructive" });
        setLoading(false);
        return;
      }

      // Admin created or already exists: sign in with the provided credentials
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        toast({
          title: "Erro",
          description: adminExists
            ? "Já existe um administrador. Email ou palavra-passe incorretos."
            : signInError.message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      toast({ title: adminExists ? "Bem-vindo(a)!" : "Admin criado!", description: "A abrir o painel..." });
      navigate("/admin");
    } catch (err: any) {
      toast({ title: "Erro", description: err.message || "Erro ao criar admin", variant: "destructive" });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-3 mb-4">
            <img src={logo} alt="Mente Sã" className="h-14 w-14 rounded-full object-cover" />
            <span className="font-display text-3xl font-bold text-primary">Mente Sã</span>
          </a>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Shield size={18} className="text-primary" />
            <p className="text-muted-foreground font-medium">Configuração Inicial, Admin</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl bg-card border border-border p-8 space-y-5">
          <p className="text-sm text-muted-foreground text-center">
            Crie a conta de administrador para gerir o sistema Mente Sã.
          </p>
          <div className="space-y-2">
            <Label htmlFor="email">Email do Admin</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@mentesa.pt"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Palavra-passe</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
              required
              minLength={6}
            />
          </div>
          <Button type="submit" className="w-full rounded-full" disabled={loading}>
            {loading ? "A criar..." : "Criar Conta Admin"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminSetup;
